import { useEffect, useRef, useState } from "react";

import {
  MarketingSection,
  SectionIntro,
  SectionKicker,
  SectionTitle,
} from "@/components/marketing/MarketingSection";
import LoopVideo from "@/components/marketing/motion/LoopVideo";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";

const EXAMPLES = [
  "boatman",
  "car",
  "crown",
  "cyber",
  "moon",
  "tent",
  "tornado",
  "wolf",
];

const MEDIA_CLASS =
  "absolute inset-0 block size-full object-cover transition-opacity duration-200";

function GalleryItem({
  name,
  tapToPlay,
}: {
  name: string;
  tapToPlay: boolean;
}) {
  const itemRef = useRef<HTMLButtonElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const label = name.charAt(0).toUpperCase() + name.slice(1);
  const poster = `/marketing/motion/gallery/${name}.webp`;

  useEffect(() => {
    const item = itemRef.current;
    if (!item || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setLoaded(true);
          else setPlaying(false);
        });
      },
      { rootMargin: "240px 0px", threshold: 0.1 },
    );
    observer.observe(item);
    return () => observer.disconnect();
  }, []);

  const setActive = (active: boolean) => {
    if (active) setLoaded(true);
    setPlaying(active);
  };

  const handlers = tapToPlay
    ? { onClick: () => setActive(!playing) }
    : {
        onPointerEnter: () => setActive(true),
        onPointerLeave: () => setActive(false),
        onFocus: () => setActive(true),
        onBlur: () => setActive(false),
      };

  return (
    <button
      ref={itemRef}
      type="button"
      aria-label={`Play ${label} motion artwork`}
      aria-pressed={playing}
      className="relative aspect-square w-full cursor-pointer overflow-hidden rounded-[16px] border border-[#DCE1EB] bg-[#F4F6FA] p-0 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-[rgba(49,67,238,0.28)]"
      {...handlers}
    >
      <img
        src={poster}
        alt={`${label} cover artwork`}
        loading="lazy"
        className={cn(MEDIA_CLASS, playing && "opacity-0")}
      />
      <LoopVideo
        src={loaded ? `/marketing/motion/gallery/${name}.mp4` : undefined}
        playing={playing}
        preload="none"
        poster={poster}
        aria-hidden
        className={cn(MEDIA_CLASS, !playing && "opacity-0")}
      />
      <span
        aria-hidden
        className={cn(
          "absolute bottom-3 right-3 z-[2] grid size-9 place-items-center rounded-full border border-white/[0.72] bg-[rgba(7,12,42,0.62)] font-open-sans text-[13px] text-white backdrop-blur-[5px] transition-[opacity,transform] duration-200",
          playing && "scale-90 opacity-0",
        )}
      >
        ▶
      </span>
    </button>
  );
}

export default function MotionExamples() {
  const tapToPlay = useMediaQuery("(hover: none), (pointer: coarse)");

  return (
    <MarketingSection className="bg-white">
      <SectionKicker>Made in Lutely</SectionKicker>
      <SectionTitle>Your artwork. A new dimension.</SectionTitle>
      <SectionIntro>
        These are real motion artwork examples created in Lutely. Hover or tap
        to bring each cover to life.
      </SectionIntro>
      <div className="mt-12 grid grid-cols-4 gap-[22px] max-[680px]:grid-cols-2 max-[680px]:gap-[14px]">
        {EXAMPLES.map((name) => (
          <GalleryItem key={name} name={name} tapToPlay={tapToPlay} />
        ))}
      </div>
    </MarketingSection>
  );
}
